import React, { useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';

import { CircularProgress } from '@mui/material';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';

import { getLogger } from 'app-components/utils/core';

import billPresentmentApi from 'data/billPresentment/api';
import { useBillersWithLogos } from 'data/billPresentment/hooks';
import type { Biller } from 'data/billPresentment/types';

import DefaultLogo from 'assets/illustrations/Default_biller_logo.svg';
import Dump from 'components/Dump';

const useStyles = makeStyles()((theme) => ({
  billerSpace: {
    display: 'flex',
    flexWrap: 'wrap',
    margin: '0 -12px',
    gap: 12,
    justifyContent: 'center',
  },
  centered: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
    minHeight: 250,
  },
  billerButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    height: 64,
    width: 164,
    padding: 8,
    backgroundColor: theme.palette.background.layer1,
    borderRadius: 12,
    border: `1px solid ${theme.palette.border.light}`,
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, border 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      border: `4px solid ${theme.palette.primary.main}`,
    },
  },
  logoImage: {
    border: `1px solid ${theme.palette.border.light}`,
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    borderRadius: 8,
  },

  logoText: {
    marginLeft: 8,
    display: '-webkit-box',
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2, // limits to 2 lines
    lineHeight: '1.3em',
    maxHeight: '2.6em',
    textOverflow: 'ellipsis',
  },
}));

// Create a type for the props of the component
interface PopularBillersGridProps {
  handleSelect: (biller: Biller) => void;
}

const log = getLogger('BillPresentment/PopularBillersGrid');

export const PopularBillersGrid = React.memo(({ handleSelect }: PopularBillersGridProps) => {
  const { classes } = useStyles();
  const [billers, setBillers] = React.useState<Biller[]>([]);
  const [hasFetched, setHasFetched] = React.useState(false);

  const getPopularBillers = async () => {
    try {
      const response = await billPresentmentApi.getPopularBillers();
      return response;
    } catch (error) {
      log.error('Error fetching popular billers:', error);
      return [];
    }
  };

  useEffect(() => {
    const fetchBillers = async () => {
      const popularBillers = await getPopularBillers();
      setBillers(popularBillers);
      setHasFetched(true);
    };
    fetchBillers();
  }, []);

  const { billersWithLogos, isLoading, error } = useBillersWithLogos(billers);
  const allLogosLoaded = React.useMemo(() => {
    return (
      !isLoading && billersWithLogos.length >= 16 && billersWithLogos.slice(0, 16).every((biller) => !!biller.logoKey)
    );
  }, [isLoading, billersWithLogos]);

  if (error) {
    return (
      <div className={classes.centered}>
        <Typography variant='paragraphRegular'>Error loading popular billers</Typography>
        <Button onClick={getPopularBillers}>Retry</Button>
      </div>
    );
  }

  if (!hasFetched || !allLogosLoaded) {
    return (
      <div className={classes.centered}>
        <CircularProgress size={150} />
      </div>
    );
  }

  if (billersWithLogos.length === 0) {
    return (
      <div className={classes.centered}>
        <>
          <Typography variant='paragraphRegular'>No popular billers found</Typography>
          <Button onClick={getPopularBillers}>Retry</Button>
        </>
      </div>
    );
  }

  return (
    <div className={classes.billerSpace}>
      {billersWithLogos.slice(0, 16).map((biller: Biller) => {
        // const isLogoValid = validImageMap[biller.id];

        return (
          <ButtonBase
            className={classes.billerButton}
            key={biller.id}
            onClick={() => (!isLoading || !error ? handleSelect(biller) : undefined)}
          >
            <Dump obj={biller} />
            {isLoading && <Skeleton variant='rectangular' width={40} height={40} />}
            {!error && !isLoading && (
              <>
                <img
                  alt={biller.name}
                  src={!biller.logoKey ? DefaultLogo : biller.logoKey}
                  className={classes.logoImage}
                />
                <Typography className={classes.logoText} variant='paragraphRegular'>
                  {biller.name}
                </Typography>
              </>
            )}
          </ButtonBase>
        );
      })}
    </div>
  );
});
PopularBillersGrid.displayName = 'PopularBillersGrid';
